// packages/core/src/constants.ts

// ─── Show Target ──────────────────────────────────────────────────────────────
export const ANNUAL_SHOW_TARGET = 60
export const QUARTERLY_SHOW_TARGET = 15

// ─── Pipeline ─────────────────────────────────────────────────────────────────
export const PIPELINE_STAGE_ORDER = [
  'inquiry',
  'qualified',
  'offer_sent',
  'hold',
  'contract_issued',
  'contract_signed',
  'deposit_received',
  'confirmed',
  'advancing',
  'completed',
] as const

// SLA in hours for each stage transition
export const PIPELINE_STAGE_SLA_HOURS: Record<string, number> = {
  inquiry_to_qualified: 4,       // first response within 4 hours
  qualified_to_offer: 24,        // offer sent within 24 hours of qualifying
  offer_to_contract: 72,         // contract issued within 72h of verbal agreement
  contract_to_deposit: 168,      // deposit received within 7 days of signing
}

// ─── Fan Tiers (Smokey Robinson defaults) ─────────────────────────────────────
export const DEFAULT_TIER_WINDOW_HOURS = 48
export const DEFAULT_TOP_TIER_ADVANCE_DAYS = 90  // 3 months

// ─── Climate ──────────────────────────────────────────────────────────────────
export const SMOKEY_MIN_TEMP_CELSIUS = 18
export const SMOKEY_OUTDOOR_BUFFER_CELSIUS = 5
export const SMOKEY_PREFERRED_MONTHS = [3, 4, 5, 6, 7, 8, 9]  // April–October

// ─── Broker Sentiment ─────────────────────────────────────────────────────────
export const SENTIMENT_THRESHOLDS = {
  healthy: 75,
  good: 60,
  monitor: 40,
  at_risk: 20,
  urgent: 0,
} as const

export const STALE_DEAL_DAYS = 7     // no activity in 7 days = stalled
export const CHASE_WINDOW_HOURS = 48 // broker must follow up within 48h

// ─── Merch ────────────────────────────────────────────────────────────────────
export const MERCH_CUTOFF_HOURS_BEFORE_SHOW = 48  // pre-order closes 48h before
export const UNCOLLECTED_ORDER_GRACE_HOURS = 2    // 2h after show ends before auto-postal

// ─── Rate Limits ──────────────────────────────────────────────────────────────
export const RATE_LIMITS = {
  auth: { requests: 10, windowMinutes: 1 },
  api: { requests: 200, windowMinutes: 1 },
  assistant: { requests: 30, windowMinutes: 1 },
  travelDesk: { requests: 40, windowMinutes: 1 },
  webhooks: { requests: 1000, windowMinutes: 1 },
} as const

// ─── Pagination ───────────────────────────────────────────────────────────────
export const DEFAULT_PAGE_SIZE = 25
export const MAX_PAGE_SIZE = 100

// ─── Analytics Windows ────────────────────────────────────────────────────────
export const ANALYTICS_WINDOWS_DAYS = [7, 30, 90, 180, 365] as const

// ─── Travel & Movement Plan ──────────────────────────────────────────────────

/** Max touring party size where the whole group can travel as the artist's
 *  retinue (one unit, no muster). Above this threshold, the group musters
 *  separately and the artist always travels on their own leg. */
export const ARTIST_MUSTER_EXCEPTION_MAX_PARTY = 4

/** Minimum coach loading time in minutes, regardless of drive time to airport.
 *  Loading 12+ people with luggage onto a coach is never instantaneous. */
export const MUSTER_COACH_MIN_LOADING_MINUTES = 20

/** UK HGV driving limits (hours). Enforced by the truck planning service. */
export const HGV_MAX_DRIVING_HOURS = 9
export const HGV_MAX_WORKING_HOURS = 11

// ─── Venue Module ────────────────────────────────────────────────────────────
export const VENUE_SMALL_CAPACITY_THRESHOLD = 2000
export const VENUE_TICKETING_FEE_PERCENT = 1.5
export const VENUE_GOOGLE_REFRESH_DAYS = 90
export const VENUE_CLAIM_TOKEN_EXPIRY_DAYS = 7

// ─── Debrief Departments ─────────────────────────────────────────────────────
export const DEBRIEF_DEPARTMENTS = [
  { value: 'production', label: 'Production', visibleTo: ['management_and_ops'] },
  { value: 'sound', label: 'Sound', visibleTo: ['sound'] },
  { value: 'lighting', label: 'Lighting & Visuals', visibleTo: ['lighting_and_visuals'] },
  { value: 'backline_stage', label: 'Backline & Stage', visibleTo: ['stage_and_backline'] },
  { value: 'artist_band', label: 'Artist & Band', visibleTo: ['artist_and_music'] },
  { value: 'wardrobe_personal', label: 'Wardrobe & Personal', visibleTo: ['wardrobe_and_personal'] },
  { value: 'management', label: 'Management', visibleTo: ['management_and_ops'] },
  { value: 'venue_promoter', label: 'Venue & Promoter', visibleTo: ['management_and_ops'] },
  { value: 'logistics', label: 'Logistics & Transport', visibleTo: ['local_production'] },
  { value: 'all_departments', label: 'All Departments', visibleTo: ['all'] },
] as const

export const DEBRIEF_DEPARTMENT_VALUES = DEBRIEF_DEPARTMENTS.map((d) => d.value)

// ─── API Retry ───────────────────────────────────────────────────────────────
export const API_RETRY_MAX_ATTEMPTS = 3
export const API_RETRY_BASE_DELAY_MS = 1000

// ─── Billing (SaaS cross-charge) ─────────────────────────────────────────────

/** Default uplift applied to third-party API costs cross-charged to org owners.
 *  Stored as integer percentage points — 20 means cost + 20%. Each Organisation
 *  row can override via `upliftPctOverride`. */
export const DEFAULT_BILLING_UPLIFT_PCT = 20

/** Chartmetric credit cost per call type — confirmed against their published
 *  pricing 2026-05-19. Standard routes = 1 credit; the listed "premium" routes
 *  = 2 credits. Recorded on every UsageLedger row for margin reconstruction.
 *
 *  Premium endpoints (per Chartmetric docs):
 *    /artist/:id/where-people-listen
 *    /artist/:id/social-audience-stats
 *    /artist/:id/instagram-audience-stats(/dates)
 *    /artist/:id/tiktok-audience-stats
 *    /artist/:id/youtube-audience-stats
 *    /artist/:id/:platform/:status/playlists
 *    /track/:id/topVideos
 */
export const CHARTMETRIC_CREDIT_COST: Record<string, number> = {
  artist_profile: 1,
  where_people_listen: 2,  // PREMIUM
  stat_spotify: 1,
  search: 1,
  similar: 1,
  artist_urls: 1,
  career: 1,
  artist_venues: 2,
  epk_snapshot: 4,         // legacy alias
  audience_bundle: 9,      // lean weekly: profile + geo + trend + urls + career + venues
  similar_refresh: 12,     // similar + enrich 5 peers + venues for top 3 peers
}

/** Chartmetric charges $0.01 per credit. Stored in pence so it composes with
 *  GBP-pence ledger maths cleanly — converted from cents elsewhere if needed. */
export const CHARTMETRIC_PENCE_PER_CREDIT = 1

/** Anthropic Sonnet-class pricing for usage ledger (pence per 1k tokens, approximate). */
export const ANTHROPIC_PENCE_PER_1K_INPUT = 3
export const ANTHROPIC_PENCE_PER_1K_OUTPUT = 15

/** Haiku gate model — much cheaper per token than Sonnet. */
export const ANTHROPIC_HAIKU_PENCE_PER_1K_INPUT = 1
export const ANTHROPIC_HAIKU_PENCE_PER_1K_OUTPUT = 4

// ─── Production public hosts (ovationhub.net) ─────────────────────────────────
/** Canonical web origin for links in email, EPK, auth, etc. Override with WEB_BASE_URL. */
export const PRODUCTION_WEB_ORIGIN = 'https://ovationhub.net'
/** Canonical API origin. Override with API_BASE_URL / API_PUBLIC_URL. */
export const PRODUCTION_API_ORIGIN = 'https://api.ovationhub.net'
/** Default inbound mailbox domain for tour/artist catch-alls. */
export const PRODUCTION_INBOUND_EMAIL_DOMAIN = 'inbox.ovationhub.net'
/** ICS UID / PRODID domain fallback. */
export const PRODUCTION_ICS_DOMAIN = 'ovationhub.net'
/** Default transactional From address when BREVO_SENDER_EMAIL unset. */
export const PRODUCTION_SENDER_EMAIL = 'info@ovationhub.net'

/** Emails that may bootstrap platform-admin access (admin role). */
export const PLATFORM_ADMIN_EMAIL_SUFFIXES = [
  '@ovationhub.net',
  '@geekybee.net',
] as const

export function isPlatformAdminEmail(email: string | null | undefined): boolean {
  if (!email) return false
  const lower = email.toLowerCase()
  return PLATFORM_ADMIN_EMAIL_SUFFIXES.some((suffix) => lower.endsWith(suffix))
}

// ─── API Cache TTLs (seconds) ────────────────────────────────────────────────
export const API_CACHE_TTL = {
  googlePlacesVenue: 60 * 60 * 24 * 30,   // 30 days
  songkickVenue: 60 * 60 * 24 * 7,        // 7 days
  pollstarContact: 60 * 60 * 24 * 7,      // 7 days
  duffelFlightSearch: 60 * 15,             // 15 minutes
  duffelHotelSearch: 60 * 60,             // 1 hour
  climateData: 60 * 60 * 24 * 30,         // 30 days
  dueDiligenceDomain: 60 * 60 * 24,       // 24 hours
} as const
